import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { BRAND } from "../lib/api";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: "Work", id: "gallery" },
    { label: "Process", id: "process" },
    { label: "Reviews", id: "reviews" },
    { label: "Services", id: "services" },
    { label: "Tips", href: "/blog" },
    { label: "Contact", id: "contact" },
  ];

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.6, 0.05, 0.15, 1] }}
      data-testid="site-header"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "bg-brand-bg/85 backdrop-blur-xl border-b border-brand-border"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-28 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3" data-testid="logo-link">
          <img src={BRAND.logo} alt="RRR Paint & Construction logo" width="80" height="80" decoding="async" className="h-16 w-16 md:h-20 md:w-20 object-contain" />
          <div className="hidden sm:block leading-tight">
            <div className="font-display text-2xl md:text-3xl tracking-wider uppercase">RRR Paint</div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-brand-muted">
              & Construction
            </div>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) =>
            l.href ? (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="text-xs uppercase tracking-[0.25em] text-brand-muted hover:text-white transition-colors link-underline"
              >
                {l.label}
              </a>
            ) : (
              <button
                key={l.id}
                onClick={() =>
                  document.getElementById(l.id)?.scrollIntoView({ behavior: "smooth" })
                }
                data-testid={`nav-${l.id}`}
                className="text-xs uppercase tracking-[0.25em] text-brand-muted hover:text-white transition-colors link-underline"
              >
                {l.label}
              </button>
            )
          )}
        </nav>

        <div className="flex items-center gap-4">
          <a
            href={BRAND.phoneHref}
            data-testid="header-phone"
            className="hidden sm:inline-flex items-center gap-2 border border-brand-blue text-white bg-brand-blue hover:bg-brand-blueHover px-5 py-3 font-display tracking-wider uppercase text-sm transition-colors"
          >
            <Phone className="h-4 w-4" />
            {BRAND.phone}
          </a>
          <button
            onClick={() => setOpen(true)}
            className="md:hidden text-white"
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-brand-bg flex flex-col p-8"
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center mb-16">
              <img src={BRAND.logo} alt="RRR" className="h-10 w-10" />
              <button onClick={() => setOpen(false)} data-testid="close-mobile-menu">
                <X className="h-7 w-7" />
              </button>
            </div>
            <nav className="flex flex-col gap-6">
              {links.map((l) =>
                l.href ? (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="font-display text-5xl uppercase tracking-tight text-left"
                  >
                    {l.label}
                  </a>
                ) : (
                  <button
                    key={l.id}
                    onClick={() => {
                      document.getElementById(l.id)?.scrollIntoView({ behavior: "smooth" });
                      setOpen(false);
                    }}
                    className="font-display text-5xl uppercase tracking-tight text-left"
                  >
                    {l.label}
                  </button>
                )
              )}
            </nav>
            <a
              href={BRAND.phoneHref}
              className="mt-auto bg-brand-blue text-white font-display text-3xl uppercase px-6 py-6 tracking-wider text-center"
            >
              {BRAND.phone}
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
